import { Plugin, PluginKey, type EditorState } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';

import type { GrammarIssue } from './types';

type GrammarPluginState = {
  decorations: DecorationSet;
};

type GrammarMeta = {
  decorations: DecorationSet;
};

export const grammarPluginKey = new PluginKey<GrammarPluginState>('grammar');

const SPELLING_KINDS = new Set(['Spelling', 'Typo']);

export function grammarPlugin(): Plugin<GrammarPluginState> {
  return new Plugin<GrammarPluginState>({
    key: grammarPluginKey,
    state: {
      init: () => ({ decorations: DecorationSet.empty }),
      apply(tr, value) {
        const meta = tr.getMeta(grammarPluginKey) as GrammarMeta | undefined;
        if (meta) return { decorations: meta.decorations };
        if (tr.docChanged) {
          return { decorations: value.decorations.map(tr.mapping, tr.doc) };
        }
        return value;
      },
    },
    props: {
      decorations(state) {
        return grammarPluginKey.getState(state)?.decorations;
      },
    },
  });
}

export function buildGrammarDecorations(issues: GrammarIssue[]): Decoration[] {
  return issues.map((issue) => {
    const severity = SPELLING_KINDS.has(issue.kind) ? 'spelling' : 'grammar';
    return Decoration.inline(
      issue.from,
      issue.to,
      { class: `pm-grammar-issue pm-grammar-issue--${severity}` },
      { issue },
    );
  });
}

/** Find the grammar issue covering `pos`, with its current (remapped) range. */
export function grammarIssueAt(
  state: EditorState,
  pos: number,
): GrammarIssue | null {
  const decorations = grammarPluginKey.getState(state)?.decorations;
  if (!decorations) return null;
  const hit = decorations.find(pos, pos)[0];
  if (!hit) return null;
  const issue = (hit.spec as { issue?: GrammarIssue }).issue;
  if (!issue) return null;
  // from/to from the decoration, which DecorationSet keeps mapped through
  // edits — the issue object itself holds the positions from lint time.
  return { ...issue, from: hit.from, to: hit.to };
}
